import { getOperationAST, OperationTypeNode, parse } from 'graphql';
import {
  ClientError,
  GraphQLClient,
  type RequestOptions,
  type TypedDocumentString,
  type Variables,
} from 'graphql-request';
import { z } from 'zod';

import {
  CustomerActionBlockedError,
  impersonationSession,
  refuseCustomerAction,
} from '../account/impersonation';
import { getAnonymousId } from '../shared/trackers';
import { normalizeZeusResult, type ZeusResult } from './zeusResult';

let visitorClient: GraphQLClient | undefined;
let supportClient: GraphQLClient | undefined;
let visitorPartialDataClient: GraphQLClient | undefined;
let supportPartialDataClient: GraphQLClient | undefined;

const READ_RETRY_DELAYS_MS = [100, 250] as const;

const createClient = (support: boolean, errorPolicy?: 'all') => {
  if (typeof window === 'undefined') {
    throw new Error('The Cosmos Zeus visitor client requires a browser origin');
  }

  return new GraphQLClient(
    new URL(support ? '/api/zeus/customer-support' : '/api/zeus', window.location.href).toString(),
    {
      credentials: 'include',
      ...(errorPolicy ? { errorPolicy } : {}),
      headers: {
        'apollographql-client-name': 'cosmos',
        'apollographql-client-version': process.env.NEXT_PUBLIC_VERSION ?? 'local',
      },
    },
  );
};

const getClient = (support: boolean) => {
  if (support) {
    supportClient ??= createClient(true);
    return supportClient;
  }
  visitorClient ??= createClient(false);
  return visitorClient;
};

const getPartialDataClient = (support: boolean) => {
  if (support) {
    supportPartialDataClient ??= createClient(true, 'all');
    return supportPartialDataClient;
  }
  visitorPartialDataClient ??= createClient(false, 'all');
  return visitorPartialDataClient;
};

type OperationMetadata = Readonly<{ name: string; type: OperationTypeNode }>;
const operationMetadataCache = new WeakMap<object, OperationMetadata>();
const operationMetadata = <TResult, TVariables extends Variables>(
  document: TypedDocumentString<TResult, TVariables>,
): OperationMetadata => {
  const cached = operationMetadataCache.get(document);
  if (cached) return cached;
  const operation = getOperationAST(parse(document.toString()));
  if (!operation) {
    throw new Error('Zeus browser requests require exactly one operation');
  }
  const metadata = {
    name: operation.name?.value ?? 'Customer action',
    type: operation.operation,
  };
  operationMetadataCache.set(document, metadata);
  return metadata;
};

type BrowserRequestContext = Readonly<{
  customerUUID: string | null;
  metadata: OperationMetadata;
  revision: number;
}>;

const createRequestContext = <TResult, TVariables extends Variables>(
  document: TypedDocumentString<TResult, TVariables>,
): BrowserRequestContext => {
  const snapshot = impersonationSession.getSnapshot();
  const metadata = operationMetadata(document);
  if (
    snapshot.customerUUID &&
    metadata.type === OperationTypeNode.MUTATION &&
    !snapshot.writeModeEnabled
  ) {
    refuseCustomerAction(metadata.name);
  }
  return { customerUUID: snapshot.customerUUID, metadata, revision: snapshot.revision };
};

const assertRequestContext = (context: BrowserRequestContext): void => {
  const snapshot = impersonationSession.getSnapshot();
  if (snapshot.customerUUID !== context.customerUUID) {
    throw new CustomerActionBlockedError('The customer support target changed during the request');
  }
  if (
    context.customerUUID &&
    context.metadata.type === OperationTypeNode.MUTATION &&
    snapshot.revision !== context.revision
  ) {
    throw new CustomerActionBlockedError('Write mode changed during the request');
  }
};

const requestLifetime = (context: BrowserRequestContext, signal?: AbortSignal) => {
  const controller = new AbortController();
  const abort = () => controller.abort(signal?.reason);
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const unsubscribe = impersonationSession.subscribe(() => {
    try {
      assertRequestContext(context);
    } catch (error) {
      controller.abort(error);
    }
  });
  return {
    signal: controller.signal,
    dispose: () => {
      unsubscribe();
      signal?.removeEventListener('abort', abort);
    },
  };
};

const isForbidden = (error: unknown): boolean =>
  error instanceof ClientError &&
  (error.response.errors ?? []).some(
    (graphQLError) => graphQLError.extensions?.['code'] === 'FORBIDDEN',
  );

const mutationFieldsSchema = z.record(z.string(), z.unknown());
const unauthorizedResultSchema = z.object({ __typename: z.literal('UnauthorizedError') });
const hasUnauthorizedPayload = (data: unknown): boolean => {
  const fields = mutationFieldsSchema.safeParse(data);
  return (
    fields.success &&
    Object.values(fields.data).some((value) => unauthorizedResultSchema.safeParse(value).success)
  );
};

const requestHeaders = (customerUUID: string | null): HeadersInit => {
  if (customerUUID) return { 'x-zeus-act-as-customer-uuid': customerUUID };
  const anonymousId = getAnonymousId();
  return anonymousId ? { 'x-opendoor-anonymous-id': anonymousId } : {};
};

export const requestZeusFromBrowser = async <TResult, TVariables extends Variables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  signal?: AbortSignal,
): Promise<TResult> => {
  const context = createRequestContext(document);
  const lifetime = requestLifetime(context, signal);
  const options: RequestOptions<Variables, TResult> = {
    document,
    variables,
    requestHeaders: requestHeaders(context.customerUUID),
    signal: lifetime.signal,
  };

  try {
    lifetime.signal.throwIfAborted();
    const result = await getClient(context.customerUUID !== null).request<TResult>(options);
    lifetime.signal.throwIfAborted();
    assertRequestContext(context);
    if (
      context.customerUUID &&
      context.metadata.type === OperationTypeNode.MUTATION &&
      hasUnauthorizedPayload(result)
    ) {
      refuseCustomerAction(context.metadata.name);
    }
    return result;
  } catch (error) {
    assertRequestContext(context);
    if (
      context.customerUUID &&
      context.metadata.type === OperationTypeNode.MUTATION &&
      isForbidden(error)
    ) {
      refuseCustomerAction(context.metadata.name);
    }
    throw error;
  } finally {
    lifetime.dispose();
  }
};

export const isRetryableZeusReadError = (error: unknown): boolean =>
  error instanceof TypeError && !(error instanceof ClientError) && error.name !== 'AbortError';

export const requestZeusReadFromBrowser = async <TResult, TVariables extends Variables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  signal?: AbortSignal,
): Promise<TResult> => {
  const context = createRequestContext(document);
  let retryIndex = 0;

  while (true) {
    signal?.throwIfAborted();
    assertRequestContext(context);
    try {
      return await requestZeusFromBrowser(document, variables, signal);
    } catch (error) {
      const retryDelayMs = READ_RETRY_DELAYS_MS[retryIndex];
      if (retryDelayMs === undefined || signal?.aborted || !isRetryableZeusReadError(error)) {
        throw error;
      }

      retryIndex += 1;
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
  }
};

interface ZeusBrowserPartialDataRequestOptions<TResult, TVariables extends Variables> {
  /** Generated operation whose successful fields should remain usable alongside GraphQL errors. */
  document: TypedDocumentString<TResult, TVariables>;
  /** Variables for the generated operation. */
  variables: TVariables;
  /** Optional signal used to cancel the browser request. */
  signal?: AbortSignal;
}

export const requestZeusFromBrowserAllowPartialData = async <
  TResult,
  TVariables extends Variables,
>({
  document,
  variables,
  signal,
}: ZeusBrowserPartialDataRequestOptions<TResult, TVariables>): Promise<ZeusResult<TResult>> => {
  const context = createRequestContext(document);
  const lifetime = requestLifetime(context, signal);
  try {
    lifetime.signal.throwIfAborted();
    const result = await getPartialDataClient(context.customerUUID !== null).rawRequest<TResult>({
      query: document.toString(),
      variables,
      requestHeaders: requestHeaders(context.customerUUID),
      signal: lifetime.signal,
    });
    lifetime.signal.throwIfAborted();
    assertRequestContext(context);
    if (
      context.customerUUID &&
      context.metadata.type === OperationTypeNode.MUTATION &&
      ((result.errors ?? []).some((error) => error.extensions?.['code'] === 'FORBIDDEN') ||
        hasUnauthorizedPayload(result.data))
    ) {
      refuseCustomerAction(context.metadata.name);
    }
    return normalizeZeusResult(result);
  } catch (error) {
    assertRequestContext(context);
    if (
      context.customerUUID &&
      context.metadata.type === OperationTypeNode.MUTATION &&
      isForbidden(error)
    ) {
      refuseCustomerAction(context.metadata.name);
    }
    throw error;
  } finally {
    lifetime.dispose();
  }
};
